import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FormControl,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  TextField,
  InputAdornment,
  Snackbar,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";

import { RxCross1, RxDoubleArrowDown, RxDoubleArrowUp } from "react-icons/rx";
import { FaHandHoldingUsd } from "react-icons/fa";
import { BiDollarCircle } from "react-icons/bi";
import { SlArrowRight } from "react-icons/sl";
import { FiBell } from "react-icons/fi";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import Trash from "../../../assets/icons/trash.svg";
import {
  // fetchData,
  fetchMultipleOutlet,
  getTransactionStatistics,
  getTransactionStatisticsNew,
  getMultipleOutlet,
  createDisbursment,
  sendOtpDisbursment,
} from "../../../services/dashboardApi";
import moment from "moment";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CircularLoading from "../../common/particles.jsx/CircularLoading";
import DatePicker from "react-datepicker";
import NotificationAlertStatusCode from "./NotificationAlertStatusCode";
import ModalSIPA from "../../common/particles.jsx/ModalSIPA";
import ModalSIA from "../../common/particles.jsx/ModalSIA";
import { getUserProfile } from "../../../services/authApi";
import { checkStatusByEmail } from "../../../services/authApi";
import NotificationAlertStatusUserCode from "./NotificationAlertStatusUserCode";
import LoadingBackdrop from "../../common/particles.jsx/LoadingBackdrop";

const toApiDate = (date) => moment(date).format("YYYY-MM-DD");
const toDate = (date) => moment(date).format("DD-MM-YYYY");

const FETCH_LIMIT = 10;

const OverviewCard = ({
  title,
  value,
  decrease,
  increase,
  isLoading,
  isRupiah,
}) => {
  return (
    <div className="p-3 border rounded-lg">
      <p className="text-[14px]">{title}</p>
      {isLoading ? (
        <CircularProgress size={20} />
      ) : (
        <div>
          <p className="mt-2 font-bold text-[20px]">
            {isRupiah ? "Rp " : ""}
            {isRupiah
              ? (value ? value : 0).toLocaleString("id-ID", {
                  useGrouping: true,
                })
              : value}
          </p>
          <div className="flex flex-wrap gap-3 mt-2">
            {decrease > increase ? (
              <div className="flex flex-row items-center ">
                <RxDoubleArrowDown className="text-red-500" />
                <p>{Math.floor(decrease)}% Penurunan</p>
              </div>
            ) : (
              <div className="flex flex-row items-center ">
                <RxDoubleArrowUp className="text-green-500" />
                <p>{Math.floor(increase)}% Peningkatan</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const name = localStorage.getItem("name");
  const company_id = localStorage.getItem("company_id");
  const titleId = localStorage.getItem("title");
  const queryClient = useQueryClient();
  const [metode, setMetode] = useState(3);
  const [outlet, setOutlet] = useState(
    Number(company_id) !== null ? [Number(company_id)] : []
  );
  const [outletBuffer, setOutletBuffer] = useState(
    Number(company_id) !== null ? [Number(company_id)] : 0
  );
  const [companyName, setCompanyName] = useState("");
  const [periode, setPeriode] = useState(8);
  const [periodSales, setPeriodSales] = useState(8);
  const [tanggalMulai, setTanggalMulai] = useState("");
  const [tanggalAkhir, setTanggalAkhir] = useState("");
  const [openSIA, setOpenSIA] = useState(false);
  const [apiMessage, setApiMessage] = useState("");

  const [openSIPA, setOpenSIPA] = useState(false);
  const [openDisburstmentModal, setOpenDisburstmentModal] = useState(false);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [createDisbursmentBody, setCreateBodyDisbursmentBody] = useState({
    total_amount: 0,
    otp: "",
  });

  const [date, setDate] = useState(false);

  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    zoomPlugin
  );

  const {
    mutate,
    data: penjualan,
    isLoading: isDashboardLoading,
  } = useMutation({
    mutationFn: fetchMultipleOutlet,
    onSuccess: () => {
      queryClient.invalidateQueries("dashboard", tanggalMulai, tanggalAkhir);
    },
  });

  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: () => getUserProfile(),
  });

  const { data: multipleOutlet } = useQuery({
    queryKey: ["multiple-outlet", companyName],
    queryFn: () => getMultipleOutlet(companyName, FETCH_LIMIT, 1),
    // onSuccess: (outlets) =>
    //   setOutlet(outlets.data.map((data) => data.company_id)),
  });

  const { data: transactionStats, isFetching: isFetchingStats } = useQuery({
    queryKey: ["transaction-stats"],
    queryFn: () => getTransactionStatisticsNew(),
  });

  const {
    mutate: mutateCreateDisburstment,
    isLoading: isCreateDisbursmentLoading,
  } = useMutation({
    mutationFn: createDisbursment,
    onSuccess: (data) => {
      setApiMessage(data.message.id);
      setOpenDisburstmentModal(false);
      setCreateBodyDisbursmentBody({
        total_amount: 0,
        otp: "",
      });
    },
    onError: (err) => setApiMessage(err.message.id),
  });

  const {
    mutate: mutateSendOtpDisburstment,
    data: otpDisbursmentData,
    isLoading: isOtpDisbursmentLoading,
  } = useMutation({
    mutationFn: sendOtpDisbursment,
    onSuccess: () => setOpenDisburstmentModal(true),
  });

  const { mutate: mutateCheckStatusByEmail, data: statusUser } = useMutation({
    mutationFn: checkStatusByEmail,
  });

  let offlineData = penjualan?.chart?.offline_order
    ? penjualan?.chart?.offline_order.sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      )
    : [];

  let onlineData = [];

  if (penjualan?.chart?.online_order) {
    onlineData = penjualan?.chart?.online_order.sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );
  }

  const labels = [
    ...new Set([
      ...onlineData.map((item) => moment(item.date).format("DD/MM/YYYY")),
      ...offlineData.map((item) => moment(item.date).format("DD/MM/YYYY")),
    ]),
  ];

  const datas = {
    labels: labels,
    datasets: [
      {
        label: "Penjualan Offline",
        data: offlineData.map((item) => item.total_order),
        borderColor: "rgb(255, 99, 132)",
        backgroundColor: "rgba(255, 99, 132, 0.5)",
      },
      {
        label: "Penjualan Online",
        data: onlineData.map((item) => item.total_order),
        borderColor: "rgb(53, 162, 235)",
        backgroundColor: "rgba(53, 162, 235, 0.5)",
      },
    ],
  };

  const onChange = useCallback(
    (dates) => {
      const [start, end] = dates;
      setStartDate(start);
      setEndDate(end);
    },
    [startDate, endDate]
  );

  const options = {
    scales: {
      x: {
        stacked: false,
      },
      y: {
        stacked: false,
      },
    },

    plugins: {
      legend: {
        position: "bottom",
      },
      title: {
        display: true,
      },
    },
  };

  useEffect(() => {
    if (profile === undefined) return;
    mutateCheckStatusByEmail({ email: profile?.email });
  }, [profile]);

  useEffect(() => {
    mutate({
      periode_sales: periode,
      method_sales: metode,
      start_date: tanggalMulai,
      end_date: tanggalAkhir,
      company_id:
        outlet.length === 0
          ? multipleOutlet?.data?.map((data) => data.company_id)
          : outlet,
    });
  }, [metode, periode, tanggalMulai, tanggalAkhir, outlet, multipleOutlet]);

  useEffect(() => {
    if (
      toApiDate(startDate) === "Invalid date" ||
      toApiDate(endDate) === "Invalid date"
    ) {
      setTanggalMulai("");
      setTanggalAkhir("");
    } else {
      setTanggalMulai(toApiDate(startDate));
      setTanggalAkhir(toApiDate(endDate));
    }
  }, [startDate, endDate]);

  return (
    <div className="mx-auto mt-16 lg:mt-0">
      <LoadingBackdrop isLoading={isOtpDisbursmentLoading} />
      <ModalSIA
        open={openSIA}
        setOpen={setOpenSIA}
        onSuccessFunc={() => {
          if (profile) mutateCheckStatusByEmail({ email: profile?.email });
        }}
      />
      <ModalSIPA
        open={openSIPA}
        setOpen={setOpenSIPA}
        onSuccessFunc={() => {
          if (profile) mutateCheckStatusByEmail({ email: profile?.email });
        }}
      />
      <div className="flex flex-wrap justify-between items-center mb-4">
        <div className="flex flex-col">
          <p className="font-semibold text-[20px]">
            Halo
            <span className="font-semibold"> {name},</span>
          </p>
          <p className="text-[14px]">Selamat datang di OLIN Dashboard anda</p>
        </div>
        {/* <FiBell className="cursor-pointer text-[24px] lg:text-[30px] hover:bg-sky-200 rounded-full p-1" /> */}
      </div>
      {statusUser !== undefined && !isDashboardLoading ? (
        <>
          <NotificationAlertStatusUserCode
            statusUserCode={statusUser.status_user_code}
          />
          <NotificationAlertStatusCode
            statusCode={statusUser.status_code}
            openSIA={openSIA}
            setOpenSIA={setOpenSIA}
            openSIPA={openSIPA}
            setOpenSIPA={setOpenSIPA}
          />
        </>
      ) : null}
      {transactionStats && transactionStats !== undefined ? (
        <div className="bg-[#EBEDF180] w-full rounded-md mt-2 mb-4 py-2 px-4 flex items-center justify-between ">
          <div className="flex items-center hover:cursor-pointer gap-4">
            <BiDollarCircle className="text-[#00CD52] text-[40px]" />
            <div>
              <p>Saldo Anda</p>
              {transactionStats.response_dashboard &&
              transactionStats.response_dashboard !== undefined &&
              !isFetchingStats ? (
                <div>
                  <div className="flex">
                    <p className="font-bold text-[14px]">
                      {transactionStats.response_dashboard.total_saldo.toLocaleString(
                        "id-ID",
                        {
                          style: "currency",
                          currency: "IDR",
                        }
                      )}
                    </p>
                  </div>
                  <p>
                    Olin Point:{" "}
                    {transactionStats.response_dashboard.loyalty_point}
                  </p>
                </div>
              ) : isFetchingStats ? (
                <CircularProgress size={20} />
              ) : null}
            </div>
          </div>
          {isFetchingStats ? null : (
            <div className="flex gap-2 items-center justify-end">
              <button
                className="py-2 px-4 bg-blue-500 text-white font-white font-bold rounded-md hover:bg-gray-300"
                type="button"
                onClick={() => {
                  mutateSendOtpDisburstment();
                }}
              >
                Tarik Saldo
              </button>
              <button
                type="button"
                className="hover:cursor-pointer hover:text-gray-300 rounded-md"
                onClick={() => navigate("/history-dashboard")}
              >
                <SlArrowRight className="text-[16px]" />
              </button>
            </div>
          )}
        </div>
      ) : null}
      <div className="border shadow-md p-3 rounded-lg">
        <h1 className="text-blue-500 font-bold text-[20px] mb-4 ">
          Laporan Penjualan
        </h1>
        {/* <hr className="border border-2 my-3" /> */}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-2 mb-4">
          <div className="flex items-center gap-4">
            <p className="font-bold whitespace-nowrap w-full basis-1/2">
              Metode Penjualan
            </p>
            <FormControl fullWidth>
              <Select
                value={metode}
                onChange={(e) => setMetode(e.target.value)}
                disabled={isDashboardLoading}
              >
                <MenuItem value={3}>Semua Penjualan</MenuItem>
                <MenuItem value={1}>Order Online</MenuItem>
                <MenuItem value={2}>Order Offline</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className="flex items-center gap-4">
            <p className="font-bold whitespace-nowrap w-full basis-1/2">
              Periode Penjualan
            </p>
            <FormControl fullWidth>
              <Select
                value={periodSales}
                disabled={isDashboardLoading}
                onChange={(e) => {
                  if (e.target.value === 7) {
                    setDate(!date);
                  } else if (e.target.value !== 7) {
                    setDate(false);
                    setStartDate(null);
                    setEndDate(null);
                  } else if (e.target.value === 10) {
                    return setPeriode(9);
                  }
                  setPeriode(e.target.value);
                  setPeriodSales(e.target.value);
                }}
              >
                <MenuItem value={8}>Hari ini</MenuItem>
                <MenuItem value={9}>Kemarin</MenuItem>
                <MenuItem value={1}>Minggu Berjalan</MenuItem>
                <MenuItem value={2}>Minggu Lalu</MenuItem>
                <MenuItem value={3}>Bulan Berjalan</MenuItem>
                <MenuItem value={4}>Bulan Lalu</MenuItem>
                <MenuItem value={5}>Tahun Berjalan</MenuItem>
                <MenuItem value={6}>Tahun Lalu</MenuItem>
                <MenuItem value={7}>Pilih Tanggal</MenuItem>
                <MenuItem value={10} disabled sx={{ display: "none" }}>
                  {toDate(tanggalMulai) + " - " + toDate(tanggalAkhir)}
                </MenuItem>
              </Select>
            </FormControl>
          </div>
          {Number(titleId) === 1 ? (
            <div className="flex items-center gap-4">
              <p className="font-bold w-full basis-1/2">Outlet</p>
              <FormControl fullWidth>
                <Select
                  disabled={isDashboardLoading}
                  value={outletBuffer}
                  onChange={(e) => setOutletBuffer(e.target.value)}
                >
                  <MenuItem
                    value={0}
                    onClick={() => {
                      multipleOutlet && multipleOutlet !== undefined
                        ? setOutlet(
                            multipleOutlet.data?.map((data) => data.company_id)
                          )
                        : setOutlet([]);
                    }}
                  >
                    Semua Outlet
                  </MenuItem>
                  {multipleOutlet && multipleOutlet !== undefined ? (
                    multipleOutlet.data?.map((data, index) => (
                      <MenuItem
                        value={data.company_id}
                        key={index}
                        onClick={() => setOutlet([data.company_id])}
                      >
                        {data.company_name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem
                      value={0}
                      disabled
                      sx={{ display: "none" }}
                    ></MenuItem>
                  )}
                </Select>
              </FormControl>
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 justify-center gap-3 mb-4">
          <OverviewCard
            title={"Pendapatan"}
            value={penjualan?.total_income?.total}
            increase={penjualan?.total_income?.increasing}
            decrease={penjualan?.total_income?.decreasing}
            isLoading={isDashboardLoading}
            isRupiah={true}
          />
          <OverviewCard
            title={"Rata-rata per Transaksi"}
            value={penjualan?.average_transaction?.total}
            increase={penjualan?.average_transaction?.increasing}
            decrease={penjualan?.average_transaction?.decreasing}
            isLoading={isDashboardLoading}
            isRupiah={true}
          />
          <OverviewCard
            title={"Transaksi"}
            value={penjualan?.count_transaction?.total}
            increase={penjualan?.count_transaction?.increasing}
            decrease={penjualan?.count_transaction?.decreasing}
            isLoading={isDashboardLoading}
            isRupiah={false}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 items-start justify-evenly gap-3">
          <div className="border shadow-md rounded-lg col-span-1 lg:col-span-3 p-4">
            <h1 className="font-bold text-[20px]">Grafik Penjualan</h1>
            {isDashboardLoading ? (
              <CircularProgress size={20} />
            ) : (
              <div className="col-span-1 lg:col-span-3 overflow-auto ">
                <div className="w-[100vh] lg:w-full h-full">
                  <Line data={datas} options={options} />
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col justify-between gap-3">
            <div className="p-3 border rounded-lg flex gap-3">
              <FaHandHoldingUsd className="text-blue-600 text-[60px] w-fit" />
              <div className="flex flex-col gap-3 font-bold">
                <p className="">Offline</p>
                {isDashboardLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <p>{penjualan?.sales_total?.offline_order}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Dialog
        open={openDisburstmentModal}
        onClose={() => setOpenDisburstmentModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          <div className="flex justify-between items-center gap-4">
            <div>
              <p className="text-[#68A1F9] text-[18px] font-semibold">
                Pencairan Saldo
              </p>
            </div>
            <RxCross1
              className="text-[16px] cursor-pointer text-red-500 hover:text-gray-300"
              onClick={() => setOpenDisburstmentModal(false)}
            />
          </div>
        </DialogTitle>
        <DialogContent>
          {otpDisbursmentData && otpDisbursmentData !== undefined ? (
            <div>
              <p>Saldo akan ditarik ke rekening penarikan berikut</p>
              <div className="flex justify-between my-3">
                <div>
                  <p>Nama</p>
                  <p>Nomor Rekening</p>
                  <p>Nama Bank</p>
                </div>
                <div>
                  <p>:</p>
                  <p>:</p>
                  <p>:</p>
                </div>
                <div>
                  <p>{otpDisbursmentData.name}</p>
                  <p>{otpDisbursmentData.nomor_rekening}</p>
                  <p>{otpDisbursmentData.name_bank}</p>
                </div>
              </div>
              <TextField
                label="Jumlah Pencairan"
                fullWidth
                value={createDisbursmentBody.total_amount}
                onChange={(e) => {
                  let numericValue = e.target.value;
                  if (numericValue === "") {
                    numericValue = "0";
                  }
                  setCreateBodyDisbursmentBody((prevValue) => ({
                    ...prevValue,
                    total_amount: parseInt(
                      numericValue.replace(/[^0-9]/g, ""),
                      10
                    ),
                  }));
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">Rp. </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="OTP"
                fullWidth
                sx={{ marginY: 2 }}
                value={createDisbursmentBody.otp}
                inputProps={{ maxLength: 4 }}
                onChange={(e) => {
                  setCreateBodyDisbursmentBody((prevValue) => ({
                    ...prevValue,
                    otp: e.target.value,
                  }));
                }}
              />
              {isCreateDisbursmentLoading ? (
                <button
                  className="w-full bg-blue-500 p-3 rounded-md text-white font-bold disabled:bg-gray-300"
                  disabled
                >
                  Loading
                </button>
              ) : (
                <button
                  className="w-full bg-blue-500 p-2 rounded-md text-white disabled:bg-gray-300"
                  disabled={
                    createDisbursmentBody.otp.length < 4 ||
                    createDisbursmentBody.total_amount < 1000
                  }
                  onClick={() =>
                    mutateCreateDisburstment(createDisbursmentBody)
                  }
                >
                  Kirim Permintaan Pencairan
                </button>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <Dialog
        open={date}
        onClose={() => {
          setDate(false);
        }}
      >
        <DialogTitle>Pilih Tanggal</DialogTitle>
        <DialogContent>
          <div className="flex">
            <DatePicker
              selected={startDate}
              onChange={onChange}
              startDate={startDate}
              endDate={endDate}
              selectsRange
              inline
            />
          </div>
          <button
            className="bg-indigo-500 rounded text-white px-2 py-1 font-semibold"
            onClick={() => {
              setDate(false);
              setPeriodSales(10);
            }}
          >
            ok
          </button>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(apiMessage)}
        autoHideDuration={5000}
        onClose={() => {
          setApiMessage("");
        }}
        message={apiMessage}
        action={
          <>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => setApiMessage("")}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </div>
  );
};

export default Dashboard;
